import { Client, IFrame, IMessage, StompSubscription } from "@stomp/stompjs";
import { resolveActiveHospitalId } from "../utils";
import {
  RealtimeEventPayload,
  RealtimeListener,
} from "./realtimeTypes";
import { REALTIME_DEBUG_LOG } from "./realtimeConfig";

const TOKEN_KEY = "token-name";
const TOKEN_HEADER = "doctor-admin-token";
const HOSPITAL_CONTEXT_HEADER = "X-Hospital-Id";
const FALLBACK_POLL_MS = 15_000;
const CONVERSATION_SAFETY_POLL_MS = 12_000;
const DELIVERY_STALE_MS = 45_000;

type DestinationListeners = Map<string, Set<RealtimeListener>>;

let client: Client | null = null;
let connectPromise: Promise<void> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let boundHospitalId: string | null = null;
let connected = false;
let connectedAt = 0;
let lastAnyEventAt = 0;
let lastInboxEventAt = 0;
let lastConversationEventAt = 0;
const connectionListeners = new Set<(value: boolean) => void>();
const listeners: DestinationListeners = new Map();
const stompSubscriptions = new Map<string, StompSubscription>();

function resolveWsUrl(token: string): string {
  const apiBase = (process.env.REACT_APP_URL || window.location.origin).replace(/\/$/, "");
  const url = new URL(apiBase);
  const protocol = url.protocol === "https:" ? "wss:" : "ws:";
  const basePath = url.pathname.replace(/\/$/, "");
  const hospitalId = resolveActiveHospitalId();
  const hospitalQuery = hospitalId
    ? `&hospitalId=${encodeURIComponent(hospitalId)}`
    : "";
  return `${protocol}//${url.host}${basePath}/ws/realtime/websocket?${TOKEN_HEADER}=${encodeURIComponent(token)}${hospitalQuery}`;
}

function resolveConnectHeaders(token: string): Record<string, string> {
  const headers: Record<string, string> = { [TOKEN_HEADER]: token };
  const hospitalId = resolveActiveHospitalId();
  if (hospitalId) {
    headers[HOSPITAL_CONTEXT_HEADER] = hospitalId;
  }
  return headers;
}

function markEventReceived(destination: string) {
  const now = Date.now();
  lastAnyEventAt = now;
  if (destination.includes("/conversation/")) {
    lastConversationEventAt = now;
  } else if (destination.includes("/hospital/") || destination.includes("/user/")) {
    lastInboxEventAt = now;
  }
}

function isChannelDelivering(lastAt: number, connectedAtMs: number): boolean {
  if (!connected) return false;
  // Connected and recently subscribed: treat as healthy even before the first
  // event arrives. Without this, every consumer ran fallback REST polls forever
  // until some unrelated notification arrived (and kept re-rendering hire).
  if (lastAt <= 0) {
    return connectedAtMs > 0 && Date.now() - connectedAtMs < DELIVERY_STALE_MS;
  }
  return Date.now() - lastAt < DELIVERY_STALE_MS;
}

function setConnected(value: boolean) {
  if (connected === value) return;
  connected = value;
  if (value) {
    connectedAt = Date.now();
    lastAnyEventAt = 0;
    lastInboxEventAt = 0;
    lastConversationEventAt = 0;
  } else {
    connectedAt = 0;
    stompSubscriptions.clear();
  }
  connectionListeners.forEach((listener) => {
    try {
      listener(value);
    } catch {
      // isolate listener failures
    }
  });
}

export function isRealtimeConnected(): boolean {
  return connected;
}

/** True when any subscribed channel recently delivered an event. */
export function isRealtimeDelivering(): boolean {
  return isChannelDelivering(lastAnyEventAt, connectedAt);
}

/** Inbox (/topic/hospital/*) delivery health — feed events must not mask inbox failures. */
export function isInboxRealtimeDelivering(): boolean {
  return isChannelDelivering(lastInboxEventAt, connectedAt);
}

/** Open-thread (/topic/conversation/*) delivery health. */
export function isConversationRealtimeDelivering(): boolean {
  return isChannelDelivering(lastConversationEventAt, connectedAt);
}

export function onRealtimeConnectionChange(
  listener: (connected: boolean) => void,
): () => void {
  connectionListeners.add(listener);
  listener(connected);
  return () => {
    connectionListeners.delete(listener);
  };
}

function parseEvent(message: IMessage): RealtimeEventPayload | null {
  try {
    return JSON.parse(message.body) as RealtimeEventPayload;
  } catch {
    return null;
  }
}

function dispatch(destination: string, event: RealtimeEventPayload) {
  markEventReceived(destination);
  if (REALTIME_DEBUG_LOG) {
    console.info("[statdoctor-realtime]", destination, event.type, event);
  }
  const subs = listeners.get(destination);
  if (!subs) return;
  subs.forEach((fn) => {
    try {
      fn(event);
    } catch {
      // isolate listener failures
    }
  });
}

function ensureBrokerSubscription(destination: string) {
  if (!client?.connected) {
    return;
  }
  const existing = stompSubscriptions.get(destination);
  if (existing) {
    return;
  }
  try {
    const sub = client.subscribe(destination, (message: IMessage) => {
      const event = parseEvent(message);
      if (event) dispatch(destination, event);
    });
    stompSubscriptions.set(destination, sub);
  } catch {
    stompSubscriptions.delete(destination);
    setConnected(false);
  }
}

function resubscribeAll() {
  stompSubscriptions.forEach((sub) => sub.unsubscribe());
  stompSubscriptions.clear();
  if (!client?.connected) return;
  listeners.forEach((_set, destination) => {
    ensureBrokerSubscription(destination);
  });
}

function activeHospitalIdOrNull(): string | null {
  return resolveActiveHospitalId() || null;
}

/** Reconnect after hospital switcher changes active context (updates X-Hospital-Id). */
export function reconnectForHospitalContextChange() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  connectPromise = null;
  boundHospitalId = null;
  stompSubscriptions.forEach((sub) => sub.unsubscribe());
  stompSubscriptions.clear();
  client?.deactivate();
  client = null;
  setConnected(false);
}

async function ensureConnected(): Promise<void> {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    throw new Error("Missing hospital auth token");
  }

  const activeHospitalId = activeHospitalIdOrNull();
  if (
    client?.connected &&
    boundHospitalId !== null &&
    activeHospitalId !== null &&
    boundHospitalId !== activeHospitalId
  ) {
    reconnectForHospitalContextChange();
  }

  if (client?.connected) {
    return;
  }

  if (connectPromise) {
    return connectPromise;
  }

  connectPromise = new Promise<void>((resolve, reject) => {
    const nextClient = new Client({
      brokerURL: resolveWsUrl(token),
      connectHeaders: resolveConnectHeaders(token),
      reconnectDelay: 0,
      heartbeatIncoming: 25000,
      heartbeatOutgoing: 25000,
      onConnect: () => {
        connectPromise = null;
        boundHospitalId = activeHospitalIdOrNull();
        setConnected(true);
        if (REALTIME_DEBUG_LOG) {
          console.info("[statdoctor-realtime] STOMP connected", nextClient.brokerURL);
        }
        resubscribeAll();
        resolve();
      },
      onStompError: (frame: IFrame) => {
        connectPromise = null;
        setConnected(false);
        const message = frame.headers.message || "STOMP connection failed";
        logRealtimeError("STOMP error", message, {
          command: frame.command,
          headers: frame.headers,
          wsUrl: nextClient.brokerURL,
        });
        reject(new Error(message));
      },
      onWebSocketClose: (event) => {
        connectPromise = null;
        setConnected(false);
        logRealtimeError("WebSocket closed", event?.reason || "closed", {
          code: event?.code,
          wsUrl: nextClient.brokerURL,
        });
        scheduleReconnect();
      },
      onWebSocketError: (event) => {
        connectPromise = null;
        setConnected(false);
        logRealtimeError("WebSocket error", event, { wsUrl: nextClient.brokerURL });
        scheduleReconnect();
      },
    });

    client = nextClient;
    nextClient.activate();
  });

  return connectPromise;
}

function logRealtimeError(phase: string, error: unknown, extra?: Record<string, unknown>) {
  if (!REALTIME_DEBUG_LOG) return;
  console.error("[statdoctor-realtime]", phase, error, extra ?? "");
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (listeners.size === 0) return;
    void ensureConnected().catch((error) => {
      logRealtimeError("reconnect failed", error);
      scheduleReconnect();
    });
  }, 3000);
}

/** Call once after login so DevTools → Network → WS shows the STOMP socket immediately. */
export async function bootstrapRealtimeConnection(): Promise<void> {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    logRealtimeError("bootstrap skipped", "no auth token");
    return;
  }
  const wsUrl = resolveWsUrl(token);
  if (REALTIME_DEBUG_LOG) {
    console.info("[statdoctor-realtime] bootstrapping WebSocket", wsUrl);
  }
  try {
    await ensureConnected();
    if (REALTIME_DEBUG_LOG) {
      console.info("[statdoctor-realtime] bootstrap OK — filter Network tab by WS, not Fetch");
    }
  } catch (error) {
    logRealtimeError("bootstrap failed", error, { wsUrl });
    throw error;
  }
}

export function getRealtimeDebugState() {
  return {
    connected,
    connectedAt,
    boundHospitalId,
    lastAnyEventAt,
    lastInboxEventAt,
    lastConversationEventAt,
    listenerDestinations: Array.from(listeners.keys()),
    stompSubscriptions: Array.from(stompSubscriptions.keys()),
    brokerURL: client?.brokerURL ?? null,
  };
}

export function subscribeRealtime(
  destination: string,
  listener: RealtimeListener,
): () => void {
  if (!listeners.has(destination)) {
    listeners.set(destination, new Set());
  }
  listeners.get(destination)!.add(listener);

  void ensureConnected()
    .then(() => ensureBrokerSubscription(destination))
    .catch((error) => {
      logRealtimeError(`subscribe failed for ${destination}`, error);
    });

  return () => {
    const set = listeners.get(destination);
    if (!set) return;
    set.delete(listener);
    if (set.size === 0) {
      listeners.delete(destination);
      const brokerSub = stompSubscriptions.get(destination);
      brokerSub?.unsubscribe();
      stompSubscriptions.delete(destination);
    }
  };
}

function resolveHospitalInboxDestination(): string | null {
  const hospitalId = resolveActiveHospitalId();
  if (!hospitalId) return null;
  return `/topic/hospital/${hospitalId}`;
}

export function subscribeHospitalInbox(listener: RealtimeListener): () => void {
  let activeDestination: string | null = null;
  let innerUnsubscribe: (() => void) | undefined;

  const bind = () => {
    const destination = resolveHospitalInboxDestination();
    if (!destination) return;
    if (destination === activeDestination) return;

    innerUnsubscribe?.();
    if (activeDestination !== null) {
      reconnectForHospitalContextChange();
    }
    activeDestination = destination;
    innerUnsubscribe = subscribeRealtime(destination, listener);
  };

  bind();
  const contextTimer = window.setInterval(bind, 2000);

  return () => {
    window.clearInterval(contextTimer);
    innerUnsubscribe?.();
  };
}

export function subscribeConversation(
  conversationId: string,
  listener: RealtimeListener,
): () => void {
  const id = String(conversationId || "").trim();
  if (!id) {
    return () => undefined;
  }
  return subscribeRealtime(`/topic/conversation/${id}`, listener);
}

export function subscribeFeedCommunity(listener: RealtimeListener): () => void {
  return subscribeRealtime("/topic/feed/community", listener);
}

export function subscribeFeedHospital(
  accountId: string,
  listener: RealtimeListener,
): () => void {
  const id = String(accountId || "").trim();
  if (!id) {
    return () => undefined;
  }
  return subscribeRealtime(`/topic/feed/hospital/${id}`, listener);
}

export function subscribeHospital(
  accountId: string,
  listener: RealtimeListener,
): () => void {
  const id = String(accountId || "").trim();
  if (!id) {
    return () => undefined;
  }
  return subscribeRealtime(`/topic/hospital/${id}`, listener);
}

export function disconnectRealtime() {
  setConnected(false);
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  connectPromise = null;
  stompSubscriptions.forEach((sub) => sub.unsubscribe());
  stompSubscriptions.clear();
  listeners.clear();
  client?.deactivate();
  client = null;
}

export const REALTIME_FALLBACK_POLL_MS = FALLBACK_POLL_MS;
export const REALTIME_CONVERSATION_SAFETY_POLL_MS = CONVERSATION_SAFETY_POLL_MS;

if (typeof window !== "undefined") {
  (window as Window & { statdoctorRealtimeDebug?: typeof getRealtimeDebugState })
    .statdoctorRealtimeDebug = getRealtimeDebugState;
}
